<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import AceleresDisconnectedInstanceAlert from 'dashboard/components/app/AceleresDisconnectedInstanceAlert.vue';
import AccessProfileNotice from 'dashboard/components/app/AccessProfileNotice.vue';
import { hasConversationPermission } from 'dashboard/helper/conversationPermissions';
import { routeIsAccessibleFor } from 'dashboard/helper/routeHelpers';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMobileDevice } from 'dashboard/composables/useMobileDevice';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';
import {
  startAceleresActivityHeartbeat,
  stopAceleresActivityHeartbeat,
} from 'dashboard/helper/aceleresActivityHeartbeat';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import ChatList from 'dashboard/components/ChatList.vue';

const CONVERSATION_ROUTE_NAMES = new Set([
  'home',
  'account_conversations',
  'inbox_conversation',
  'inbox_dashboard',
  'conversation_through_inbox',
  'label_conversations',
  'conversations_through_label',
  'team_conversations',
  'conversations_through_team',
  'folder_conversations',
  'conversations_through_folders',
  'conversation_mentions',
  'conversation_through_mentions',
  'conversation_unattended',
  'conversation_through_unattended',
  'conversation_participating',
  'conversation_through_participating',
]);

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    AceleresDisconnectedInstanceAlert,
    AccessProfileNotice,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    MobileSidebarLauncher,
    ChatList,
  },
  provide() {
    return {
      toggleMobileSidebar: () => this.toggleMobileSidebar(),
    };
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const isMobileViewport = useMobileDevice();
    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      isMobileViewport,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    shouldShowFloatingMobileSidebarLauncher() {
      return this.$route.name !== 'home';
    },
    isConversationRoute() {
      return CONVERSATION_ROUTE_NAMES.has(this.$route.name);
    },
    canRenderCurrentRoute() {
      const user = this.$store.getters.getCurrentUser;
      return (
        routeIsAccessibleFor(
          this.$route,
          getUserPermissions(user, this.accountId)
        ) &&
        (!this.isConversationRoute ||
          hasConversationPermission(user, this.accountId, 'conversation_view'))
      );
    },
    attendancePanelRouteProps() {
      const { name, params } = this.$route;
      const conversationTypeByRoute = {
        conversation_mentions: wootConstants.CONVERSATION_TYPE.MENTION,
        conversation_through_mentions: wootConstants.CONVERSATION_TYPE.MENTION,
        conversation_unattended: wootConstants.CONVERSATION_TYPE.UNATTENDED,
        conversation_through_unattended:
          wootConstants.CONVERSATION_TYPE.UNATTENDED,
        conversation_participating:
          wootConstants.CONVERSATION_TYPE.PARTICIPATING,
        conversation_through_participating:
          wootConstants.CONVERSATION_TYPE.PARTICIPATING,
      };

      return {
        conversationInbox: params.inbox_id || 0,
        label: params.label || '',
        teamId: params.teamId || 0,
        foldersId:
          name === 'folder_conversations' ||
          name === 'conversations_through_folders'
            ? params.id
            : 0,
        conversationType: conversationTypeByRoute[name] || '',
      };
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
        'configuracoes_novo_usuario',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    '$route.fullPath'() {
      if (this.isSmallScreen) this.closeMobileSidebar();
    },
    isMobileViewport: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (this.isMobileViewport) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          // Aceleres: sem valor anterior, cai no CONDENSED (o mesmo padrão que
          // ConversationView assume quando a chave não existe). Antes ia
          // `undefined` aqui: o JSON.stringify descarta a chave, o backend
          // faz merge das ui_settings (profiles_controller) e o 'expanded'
          // gravado numa janela estreita ficava para sempre — a lista de
          // conversas do agente perdia a alça de redimensionar.
          this.updateUISettings({
            conversation_display_type:
              this.previouslyUsedDisplayType || LAYOUT_TYPES.CONDENSED,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Aceleres: emite heartbeat de atividade real (alimenta o painel Equipe Online)
    startAceleresActivityHeartbeat();
  },
  beforeUnmount() {
    stopAceleresActivityHeartbeat();
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    onPersistentConversationLoad() {
      this.$refs.dashboardRouteView?.onConversationLoad?.();
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-col flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
    >
      <AccessProfileNotice />
      <div class="flex flex-1 min-h-0 min-w-0 overflow-hidden">
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        >
          <MobileSidebarLauncher
            v-if="shouldShowFloatingMobileSidebarLauncher"
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
        </UpgradePage>
        <template v-if="!showUpgradePage">
          <router-view v-slot="{ Component }">
            <template v-if="canRenderCurrentRoute">
              <div
                v-if="isConversationRoute"
                class="flex min-h-0 min-w-0 flex-1"
                :class="{
                  'painel-atendimento acl-desktop-conversation-layout':
                    !isMobileViewport,
                }"
              >
                <ChatList
                  v-if="!isMobileViewport"
                  v-bind="attendancePanelRouteProps"
                  @conversation-load="onPersistentConversationLoad"
                />
                <div class="min-h-0 min-w-0 flex-1">
                  <component :is="Component" ref="dashboardRouteView" />
                </div>
              </div>
              <component :is="Component" v-else ref="dashboardRouteView" />
            </template>
          </router-view>
          <CommandBar />
          <CopilotLauncher />
          <MobileSidebarLauncher
            v-if="shouldShowFloatingMobileSidebarLauncher"
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
          <CopilotContainer />
        </template>
        <AddAccountModal
          :show="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <AceleresDisconnectedInstanceAlert :account-id="accountId" />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
      </div>
    </main>
  </div>
</template>
